import React, { useMemo } from "react";
import classNames from "classnames";
import { startCase } from "lodash";
import { DateTime } from "luxon";
import { GetComponentProps } from "rc-table/lib/interface";

import { TextLink } from "components/actions/Links";
import { Currency } from "components/app_layout/Typography";
import { stringSort } from "lib/columnSort";
import RedactedWrapper from "lib/core_components/RedactedWrapper";
import { commonTableColumns } from "lib/core_components/Table/ColumnTypes";
import {
  useBuyerSummary,
  useSearchSpendData,
  useSupplierGuidSummary,
  useSupplierSummaryGrouped,
} from "lib/generated/spend-data-api/spendDataManagementAPI";
import {
  BuyerSummary,
  SpendDataDto,
  SpendDataFilters as Filters,
  SupplierGuidSummary,
  SupplierSummary,
} from "lib/generated/spend-data-api/spendDataManagementAPI.schemas";
import { useSignalSettings } from "lib/hooks/api/teams/useSignalSettings";
import { TableKey, useTablePageSize } from "lib/hooks/useTablePageSize";
import { TypedColumnProps } from "lib/search/SearchTable";
import { GroupByKey } from "../config";

import css from "../Table.module.scss";

const DEFAULT_COLUMN_SIZE = { large: 240, small: 160, medium: 160, xlarge: 300 };

type TransactionTableRow = SpendDataDto & {
  descriptiveColumns?: Record<string, string>;
};

type UseSpendTable = {
  data?: TransactionTableRow[] | SupplierSummary[] | BuyerSummary[] | SupplierGuidSummary[];
  isLoading: boolean;
  columns: TypedColumnProps<
    TransactionTableRow | SupplierSummary | BuyerSummary | SupplierGuidSummary
  >[];
  rowKey: (r: TransactionTableRow | SupplierSummary | BuyerSummary | SupplierGuidSummary) => string;
  pagination?: {
    current?: number;
    pageSize?: number;
    total?: number;
    onChange: (page: number, pageSize?: number) => void;
  };
  onRow?: GetComponentProps<
    TransactionTableRow | SupplierSummary | BuyerSummary | SupplierGuidSummary
  >;
  isError: boolean;
};

export const useSpendTable = (
  filters: Filters,
  tableType: GroupByKey | undefined,
  tableKey: TableKey,
): UseSpendTable => {
  const [pageSize, setPageSize] = useTablePageSize(tableKey);
  const [currentPage, setCurrentPage] = React.useState(1);
  const offset = (currentPage - 1) * pageSize;

  const {
    data: groupedSupplierData,
    isLoading: groupedSupplierIsLoading,
    isError: groupedSupplierHasError,
  } = useSupplierSummaryGrouped(
    {
      filters,
      limit: pageSize,
      offset,
      orderBy: "total",
      sortOrder: "DESC",
    },
    { query: { enabled: tableType === "partners" || tableType === "competitors" } },
  );
  const {
    data: supplierData,
    isLoading: supplierIsLoading,
    isError: supplierHasError,
  } = useSupplierGuidSummary(
    {
      filters,
      limit: pageSize,
      offset,
      orderBy: "total",
      sortOrder: "DESC",
    },
    { query: { enabled: tableType === "allSuppliers" } },
  );
  const {
    data: buyerData,
    isLoading: buyerIsLoading,
    isError: buyerHasError,
  } = useBuyerSummary(
    {
      filters,
      limit: pageSize,
      offset,
      orderBy: "total",
      sortOrder: "DESC",
    },
    { query: { enabled: tableType === "buyers" } },
  );
  const {
    data: transactionData,
    isLoading: transactionIsLoading,
    isError: transactionHasError,
  } = useSearchSpendData(
    {
      filters,
      limit: pageSize,
      offset,
    },
    { query: { enabled: tableType === "allTransactions" } },
  );
  const { data: signalSettings } = useSignalSettings({
    enabled: tableType === "partners" || tableType === "competitors",
  });

  const result = useMemo((): UseSpendTable => {
    let { data, columns, isLoading, rowKey, pagination, onRow, isError }: UseSpendTable = {
      data: [],
      isLoading: false,
      columns: [],
      rowKey: (r) => r.toString(),
      isError: false,
    };

    switch (tableType) {
      case "partners":
      case "competitors": {
        if (groupedSupplierData && signalSettings) {
          const key = tableType === "partners" ? "Partners" : "Competitors";

          const signals = new Set(
            signalSettings.signals.filter((s) => s.category === key).map((s) => s.name),
          );

          data = groupedSupplierData.data.filter((d) => signals.has(d.supplier));
        } else {
          data = undefined;
        }
        isLoading = groupedSupplierIsLoading;
        isError = groupedSupplierHasError;
        columns = [
          {
            ...commonTableColumns.titleColumn,
            title: tableType === "partners" ? "Partner" : "Competitor",
            width: "33em",
            sizeConfig: { large: 500, small: 200, medium: 400, xlarge: 600 },
            sorter: stringSort((r) => (r as SupplierSummary).supplier),
            key: "title",
            render: (r) => (
              <RedactedWrapper
                requiredDataType={"SUPPLIERS"}
                redactContent={(r as SupplierSummary).supplier}
                contextSource={"In-row"}
              >
                <span
                  className={classNames(
                    css.supplierCell,
                    tableType === "partners" ? "tag-yellow" : "tag-red",
                  )}
                >
                  {(r as SupplierSummary).supplier}
                </span>
              </RedactedWrapper>
            ),
          },
          {
            ...commonTableColumns.valueColumn,
            title: "Total transaction value",
            render: (r) => <Currency value={r.total} />,
            sizeConfig: { large: 500, small: 200, medium: 400, xlarge: 600 },
            sorter: (a, b) => (a as SupplierSummary).total - (b as SupplierSummary).total,
            sortDirections: ["descend", "ascend"],
            defaultSortOrder: "descend",
            key: "amount",
          },
          {
            ...commonTableColumns.valueColumn,
            title: "Number of transactions",
            render: (r) => (r as SupplierSummary).totalRecords,
            sizeConfig: { large: 400, small: 100, medium: 300, xlarge: 500 },
            sorter: (a, b) =>
              (a as SupplierSummary).totalRecords - (b as SupplierSummary).totalRecords,
            key: "numTransactions",
            sortDirections: ["descend", "ascend"],
          },
        ];
        rowKey = (r) => (r as SupplierSummary).supplier;

        pagination = {
          current: currentPage,
          pageSize,
          onChange: (pageNum: number, pageSize: number | undefined) => {
            if (pageSize) setPageSize(pageSize);
            setCurrentPage(pageNum);
          },
          total: groupedSupplierData?.total,
        };

        onRow = (r) => ({
          onClick() {
            if ("supplier" in r) {
              window.location.href = `${tableType}/${r.supplier}`;
            }
          },
        });

        break;
      }
      case "allSuppliers": {
        data = supplierData?.data;
        isLoading = supplierIsLoading;
        isError = supplierHasError;
        columns = [
          {
            ...commonTableColumns.titleColumn,
            title: "Supplier",
            width: "33em",
            sizeConfig: { large: 500, small: 200, medium: 400, xlarge: 600 },
            sorter: stringSort((r) => (r as SupplierGuidSummary).supplierName),
            render: (r) => (
              <span className={css.supplierCell}>
                <TextLink to={`/suppliers/${(r as SupplierGuidSummary).supplierGuid}`}>
                  {(r as SupplierGuidSummary).supplierName}
                </TextLink>
              </span>
            ),
            key: "title",
          },
          {
            ...commonTableColumns.valueColumn,
            title: "Total transaction value",
            render: (r) => <Currency value={r.total} />,
            sizeConfig: { large: 500, small: 200, medium: 400, xlarge: 600 },
            sorter: (a, b) => (a as SupplierSummary).total - (b as SupplierSummary).total,
            defaultSortOrder: "descend",
            key: "amount",
          },
          {
            ...commonTableColumns.valueColumn,
            title: "Number of transactions",
            render: (r) => (r as SupplierSummary).totalRecords,
            sizeConfig: DEFAULT_COLUMN_SIZE,
            sorter: (a, b) =>
              (a as SupplierSummary).totalRecords - (b as SupplierSummary).totalRecords,
            key: "numTransactions",
          },
        ];
        rowKey = (r) => (r as SupplierSummary).supplier;
        break;
      }
      case "buyers": {
        data = buyerData?.data;
        isLoading = buyerIsLoading;
        isError = buyerHasError;
        columns = [
          {
            ...commonTableColumns.titleColumn,
            title: "Buyer",
            render: (r) => (
              <span className={css.nameColumnLink}>{(r as BuyerSummary).buyerName}</span>
            ),
            sizeConfig: { large: 500, small: 200, medium: 400, xlarge: 600 },
            sorter: stringSort((r) => (r as BuyerSummary).buyerName),
            key: "title",
          },
          {
            ...commonTableColumns.valueColumn,
            title: "Total transaction value",
            render: (r) => <Currency value={r.total} />,
            sizeConfig: { large: 500, small: 200, medium: 400, xlarge: 600 },
            sorter: (a, b) => (a as BuyerSummary).total - (b as BuyerSummary).total,
            defaultSortOrder: "descend",
            key: "amount",
          },
          {
            ...commonTableColumns.valueColumn,
            title: "Number of transactions",
            render: (r) => (r as BuyerSummary).totalRecords,
            sizeConfig: DEFAULT_COLUMN_SIZE,
            sorter: (a, b) => (a as BuyerSummary).totalRecords - (b as BuyerSummary).totalRecords,
            key: "numTransactions",
          },
        ];
        rowKey = (r) => (r as BuyerSummary).buyer;

        pagination = {
          current: currentPage,
          pageSize,
          onChange: (pageNum: number, pageSize: number | undefined) => {
            if (pageSize) setPageSize(pageSize);
            setCurrentPage(pageNum);
          },
          total: buyerData?.total,
        };

        onRow = (r) => ({
          onClick() {
            if ("buyer" in r) {
              window.location.href = `buyers/${r.buyer}`;
            }
          },
        });

        break;
      }
      case "allTransactions": {
        const typeMaxInstanceCounts: Record<string, number> = {};

        data =
          transactionData?.data?.map((row) => {
            const currentRowInstanceCounts: Record<string, number> = {}; // Tracks instance counts for the current row
            const descriptiveColumnsData = row.descriptiveFields?.reduce<Record<string, string>>(
              (fields, field) => {
                if (!field.type) {
                  return fields;
                }

                // Increment instance count for this field.type within the current row
                const instanceCount = (currentRowInstanceCounts[field.type] || 0) + 1;
                currentRowInstanceCounts[field.type] = instanceCount;

                // Determine the key for this field's data (e.g., "category" or "category_2")
                const dataKey = instanceCount > 1 ? `${field.type}_${instanceCount}` : field.type;
                fields[dataKey] = field.data;

                if ((typeMaxInstanceCounts[field.type] || 0) < instanceCount) {
                  typeMaxInstanceCounts[field.type] = instanceCount;
                }
                return fields;
              },
              {},
            );
            return { ...row, descriptiveColumns: descriptiveColumnsData };
          }) || [];

        isLoading = transactionIsLoading;
        isError = transactionHasError;
        const descriptiveFieldColumns: typeof columns = []; // Assuming 'columns' is correctly typed for your table

        // Generate dynamic columns based on typeMaxInstanceCounts
        for (const [baseKey, maxInstances] of Object.entries(typeMaxInstanceCounts)) {
          descriptiveFieldColumns.push({
            title: startCase(baseKey),
            render: (
              record: TransactionTableRow | SupplierSummary | BuyerSummary | SupplierGuidSummary,
            ) => {
              const cellValue = (record as TransactionTableRow).descriptiveColumns?.[baseKey];
              if (Array.isArray(cellValue)) {
                return cellValue.map((item, index: number) => (
                  <div key={index}>{String(item.data ?? item.value ?? "")}</div>
                ));
              }
              return cellValue;
            },
            sizeConfig: DEFAULT_COLUMN_SIZE,
            ellipsis: true,
            key: baseKey,
          });

          // Additional columns for subsequent instances (e.g., "category_2", "category_3")
          if (maxInstances > 1) {
            for (let i = 2; i <= maxInstances; i++) {
              const dynamicDataKey = `${baseKey}_${i}`; // Key to access data, e.g., "category_2"
              const columnTitle = startCase(dynamicDataKey.replace("_", " ")); // e.g., "Category 2"

              descriptiveFieldColumns.push({
                title: columnTitle,
                render: (
                  record:
                    | TransactionTableRow
                    | SupplierSummary
                    | BuyerSummary
                    | SupplierGuidSummary,
                ) => {
                  const cellValue = (record as TransactionTableRow).descriptiveColumns?.[
                    dynamicDataKey
                  ];
                  if (Array.isArray(cellValue)) {
                    return cellValue.map((item, index: number) => (
                      <div key={index}>{String(item.data ?? item.value ?? "")}</div>
                    ));
                  }
                  return cellValue;
                },
                sizeConfig: DEFAULT_COLUMN_SIZE,
                ellipsis: true,
                key: dynamicDataKey,
              });
            }
          }
        }

        columns = [
          {
            ...commonTableColumns.titleColumn,
            title: "Buyer",
            render: (r) => (r as TransactionTableRow).buyerName,
            sizeConfig: { large: 500, small: 200, medium: 400, xlarge: 600 },
            key: "title",
          },
          {
            ...commonTableColumns.titleColumn,
            title: "Supplier",
            render: (r) => (r as TransactionTableRow).supplier,
            sizeConfig: { large: 500, small: 200, medium: 400, xlarge: 600 },
            key: "supplier",
          },
          {
            ...commonTableColumns.valueColumn,
            title: "Total transaction value",
            render: (r) => <Currency value={r.amount} />,
            sizeConfig: { large: 300, small: 200, medium: 300, xlarge: 400 },
            key: "amount",
          },
          {
            ...commonTableColumns.dateColumn,
            title: "Date",
            render: (r) => {
              const d = (r as TransactionTableRow).date;
              return d ? DateTime.fromISO(d).toFormat("MMM d, yyyy") : null;
            },
            key: "data",

            sizeConfig: DEFAULT_COLUMN_SIZE,
          },
          ...descriptiveFieldColumns,
        ];

        rowKey = (r) => (r as SpendDataDto).id;

        pagination = {
          current: currentPage,
          pageSize,
          onChange: (pageNum: number, pageSize: number | undefined) => {
            if (pageSize) setPageSize(pageSize);
            setCurrentPage(pageNum);
          },
          total: transactionData?.total,
        };
        break;
      }
    }

    return {
      data,
      isLoading,
      columns,
      rowKey,
      pagination,
      onRow,
      isError,
    };
  }, [
    tableType,
    currentPage,
    groupedSupplierData,
    signalSettings,
    groupedSupplierIsLoading,
    groupedSupplierHasError,
    supplierData?.data,
    supplierIsLoading,
    supplierHasError,
    buyerData?.data,
    buyerData?.total,
    buyerIsLoading,
    buyerHasError,
    transactionData?.data,
    transactionData?.total,
    transactionIsLoading,
    transactionHasError,
    pageSize,
    setPageSize,
  ]);

  return result;
};
